'use client';

import { useState, useEffect } from 'react';
import { BRIEF_STEP, DEFAULT_STUDY, Study, TOTAL_STEPS } from '@/lib/study';
import { TopBar } from '@/components/setup/StepShell';
import { Step1Goal } from '@/components/setup/steps/Step1Goal';
import { Step2Deployment } from '@/components/setup/steps/Step2Deployment';
import { Step2Decision } from '@/components/setup/steps/Step2Decision';
import { Step3Aspects } from '@/components/setup/steps/Step3Aspects';
import { Step4Background } from '@/components/setup/steps/Step4Background';
import { Step5DraftQuestions } from '@/components/setup/steps/Step5DraftQuestions';
import { Step6Review } from '@/components/setup/steps/Step6Review';
import { Step7FollowUps } from '@/components/setup/steps/Step7FollowUps';
import { Step8Opening } from '@/components/setup/steps/Step8Opening';
import { Step9Closing } from '@/components/setup/steps/Step9Closing';
import { Step10Brief } from '@/components/setup/steps/Step10Brief';
import { Step11Publish } from '@/components/setup/steps/Step11Publish';
import { T } from '@/lib/tokens';

export default function SetupPage() {
  const [step, setStep] = useState(() => {
    try {
      const s = localStorage.getItem('setup_step');
      const n = s ? parseInt(s, 10) : 1;
      return Number.isFinite(n) && n >= 1 && n <= TOTAL_STEPS ? n : 1;
    } catch { return 1; }
  });
  const [study, setStudy] = useState<Study>(() => {
    try {
      const s = localStorage.getItem('setup_study');
      return s ? { ...DEFAULT_STUDY, ...JSON.parse(s) } : DEFAULT_STUDY;
    } catch { return DEFAULT_STUDY; }
  });

  useEffect(() => {
    try { localStorage.setItem('setup_step', String(step)); } catch { /* ignore */ }
  }, [step]);

  useEffect(() => {
    try { localStorage.setItem('setup_study', JSON.stringify(study)); } catch { /* ignore */ }
  }, [study]);

  const isEmbedded = study.deploymentMode === 'embedded';
  // Brief is only relevant in standalone mode — when embedded, skip past it
  // automatically in both directions so the participant brief step never shows.
  const shouldSkip = (s: number) => s === BRIEF_STEP && isEmbedded;

  const next = () =>
    setStep((s) => {
      let n = Math.min(s + 1, TOTAL_STEPS);
      if (shouldSkip(n)) n = Math.min(n + 1, TOTAL_STEPS);
      return n;
    });
  const back = () =>
    setStep((s) => {
      let p = Math.max(s - 1, 1);
      if (shouldSkip(p)) p = Math.max(p - 1, 1);
      return p;
    });

  const stepProps = { data: study, setData: setStudy, onNext: next, onBack: back };

  return (
    <div style={{ minHeight: '100vh', background: T.cream }}>
      <TopBar step={step} total={TOTAL_STEPS} />
      <div key={step} className="fadeIn">
        {step === 1 && <Step1Goal data={study} setData={setStudy} onNext={next} />}
        {step === 2 && <Step2Deployment {...stepProps} />}
        {step === 3 && <Step2Decision {...stepProps} />}
        {step === 4 && <Step3Aspects {...stepProps} />}
        {step === 5 && <Step4Background {...stepProps} />}
        {step === 6 && <Step5DraftQuestions {...stepProps} />}
        {step === 7 && <Step6Review {...stepProps} />}
        {step === 8 && <Step7FollowUps {...stepProps} />}
        {step === 9 && <Step8Opening {...stepProps} />}
        {step === 10 && <Step9Closing {...stepProps} />}
        {step === 11 && <Step10Brief {...stepProps} />}
        {step === 12 && (
          <Step11Publish data={study} setData={setStudy} onBack={back} />
        )}
      </div>
    </div>
  );
}
